import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, MapPin, Phone, Flower2, Clock, Route, Home, LogOut, MessageCircle, Camera, Check, Image as ImageIcon } from "lucide-react";
import { api, TIER_MAP, TOKEN_KEY } from "@/lib/api";
import { T } from "@/lib/testIds";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function toISODate(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; }
function formatLong(d) {
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtDuration(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60), m = min % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function buildWhatsAppText(dateISO, stops, totalMin) {
  const dt = new Date(dateISO).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long" });
  const lines = [
    `*BUDS delivery route — ${dt}*`,
    `${stops.length} stop${stops.length === 1 ? "" : "s"} · ~${fmtDuration(totalMin)} drive`,
    ``,
  ];
  stops.forEach((s) => {
    lines.push(`*${s.order}. ${s.full_name}* — ${s.preferred_time}`);
    lines.push(`Tier: ${TIER_MAP[s.tier]?.name || s.tier}${s.is_gift ? ` (gift → ${s.recipient_name})` : ""}`);
    lines.push(`📞 ${s.phone}`);
    lines.push(`📍 ${s.address}${s.apartment ? ` · ${s.apartment}` : ""}`);
    lines.push(`🗺️ https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`);
    lines.push(``);
  });
  return lines.join("\n");
}

export default function AdminCalendar() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null);
  const [uploadingStop, setUploadingStop] = useState(null);

  const load = async (ws) => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscriptions/calendar", { params: { week_start: toISODate(ws) } });
      setData(data);
    } catch { toast.error("Failed to load calendar."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(weekStart); }, [weekStart]);

  const totals = useMemo(() => {
    if (!data) return { stops: 0, minutes: 0 };
    let stops = 0, minutes = 0;
    Object.values(data.days).forEach((d) => { stops += d.total_stops; minutes += d.estimated_drive_time_min; });
    return { stops, minutes };
  }, [data]);

  const weekEnd = addDays(weekStart, 6);
  const selectedDay = openDay && data?.days?.[openDay];

  const openWhatsApp = (dayIso, day) => {
    const text = buildWhatsAppText(dayIso, day.stops, day.estimated_drive_time_min);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const refreshDay = async () => {
    await load(weekStart);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="border-b border-[var(--line-strong)] bg-[var(--bg-alt)]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-serif text-xl">BUDS<span className="italic">.</span></Link>
            <span className="hairline w-8" />
            <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]">Studio · Delivery Calendar</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/admin" className="btn-ghost"><ArrowLeft size={14} /> Members</Link>
            <button onClick={() => { localStorage.removeItem(TOKEN_KEY); window.location.href = "/admin/login"; }} className="btn-ghost"><LogOut size={14}/> Log out</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <p className="chapter-num text-5xl">§</p>
            <h1 className="font-serif text-5xl sm:text-6xl leading-none mt-2">Delivery week</h1>
            <p className="text-[var(--ink-soft)] mt-3 text-sm">
              {formatLong(weekStart)} — {formatLong(weekEnd)} · {totals.stops} stop{totals.stops === 1 ? "" : "s"} · ~{fmtDuration(totals.minutes)} total drive
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={T.admin.calendarPrev} onClick={() => setWeekStart(addDays(weekStart, -7))}
              className="w-10 h-10 flex items-center justify-center border border-[var(--line-strong)] hover:border-[var(--ink)] transition-colors duration-300">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="px-4 h-10 border border-[var(--line-strong)] hover:border-[var(--ink)] text-[11px] tracking-[0.2em] uppercase">
              This week
            </button>
            <button data-testid={T.admin.calendarNext} onClick={() => setWeekStart(addDays(weekStart, 7))}
              className="w-10 h-10 flex items-center justify-center border border-[var(--line-strong)] hover:border-[var(--ink)] transition-colors duration-300">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {loading && <div className="p-10 text-center text-sm text-[var(--ink-soft)]">Loading…</div>}

        {data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-px bg-[var(--line-strong)] border border-[var(--line-strong)]">
            {Object.entries(data.days).map(([dayIso, day]) => {
              const dt = new Date(dayIso);
              const dayLabel = DAY_LABELS[(dt.getDay() + 6) % 7];
              const isToday = toISODate(new Date()) === dayIso;
              return (
                <button
                  key={dayIso}
                  data-testid={T.admin.calendarDay(dayIso)}
                  onClick={() => setOpenDay(dayIso)}
                  className="bg-[var(--bg)] text-left p-4 min-h-[240px] flex flex-col hover:bg-[var(--bg-alt)] transition-colors duration-300"
                >
                  <div className="flex items-baseline justify-between mb-4">
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--ink-soft)] flex items-center gap-2">
                        {dayLabel}
                        {isToday && <span className="w-1.5 h-1.5 rounded-full bg-[var(--clay)]" />}
                      </p>
                      <p className="font-serif text-3xl leading-none">{dt.getDate()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--sage-deep)]">
                        {day.total_stops === 0 ? "—" : `${day.total_stops} stop${day.total_stops === 1 ? "" : "s"}`}
                      </p>
                      {day.total_stops > 0 && (
                        <p data-testid={T.admin.driveTime(dayIso)} className="text-[10px] tracking-[0.22em] uppercase text-[var(--ink-soft)] mt-1 flex items-center gap-1 justify-end">
                          <Route size={10}/> {fmtDuration(day.estimated_drive_time_min)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3 flex-1">
                    {day.total_stops === 0 && <p className="text-xs text-[var(--ink-soft)] italic">No deliveries.</p>}
                    {day.pincode_summary?.length > 0 && (
                      <div className="space-y-1">
                        {day.pincode_summary.map((p) => (
                          <p key={p.pincode} className="text-[11px] tracking-[0.12em] uppercase text-[var(--sage-deep)] flex items-center gap-1.5">
                            <MapPin size={10}/> {p.pincode} <span className="text-[var(--ink-soft)]">· {p.count}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    {day.total_stops > 0 && (
                      <p className="text-[11px] text-[var(--ink-soft)] mt-2 italic">Tap to view route →</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Day route drawer */}
      <AnimatePresence>
        {selectedDay && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[rgba(15,20,17,0.5)] backdrop-blur-[2px] flex items-stretch justify-end"
            onClick={() => setOpenDay(null)}
          >
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[760px] h-full overflow-y-auto bg-[var(--bg)] px-8 sm:px-14 py-10"
            >
              <div className="flex items-start justify-between mb-8 gap-6">
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">Route</p>
                  <h2 className="font-serif text-4xl leading-none">
                    {formatLong(new Date(openDay))}
                  </h2>
                  <p className="text-sm text-[var(--ink-soft)] mt-3 flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1"><Home size={12}/> Studio</span>
                    <span>·</span>
                    <span>{selectedDay.total_stops} stop{selectedDay.total_stops === 1 ? "" : "s"}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Clock size={12}/> ~{fmtDuration(selectedDay.estimated_drive_time_min)} total</span>
                  </p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  {selectedDay.total_stops > 0 && (
                    <button
                      data-testid={T.admin.whatsapp(openDay)}
                      onClick={() => openWhatsApp(openDay, selectedDay)}
                      className="inline-flex items-center gap-2 border border-[var(--ink)] bg-[var(--ink)] text-[var(--bg)] px-4 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-transparent hover:text-[var(--ink)] transition-colors duration-500"
                    >
                      <MessageCircle size={14}/> Share via WhatsApp
                    </button>
                  )}
                  <button onClick={() => setOpenDay(null)} aria-label="Close" className="text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors">
                    Close
                  </button>
                </div>
              </div>

              <ol className="relative border-l border-[var(--line-strong)] ml-3">
                <li className="pl-8 pb-8 relative">
                  <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[var(--ink)]" />
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--ink-soft)]">Depart · Studio</p>
                  <p className="font-serif text-xl mt-1">BUDS Studio, Bangalore</p>
                </li>

                {selectedDay.stops.map((s) => (
                  <li key={s.id} data-testid={T.admin.routeStop(s.id)} className="pl-8 pb-8 relative">
                    <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full border border-[var(--ink)] bg-[var(--bg)]" style={{ background: s.delivered_today ? "var(--ink)" : "var(--bg)" }} />
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--sage-deep)] flex items-center gap-3 flex-wrap">
                      <span>#{s.order}</span>
                      <span className="text-[var(--ink-soft)] flex items-center gap-1"><Clock size={10}/> +{s.leg_minutes}m</span>
                      <span className="text-[var(--ink-soft)] flex items-center gap-1"><MapPin size={10}/> {s.pincode}</span>
                      <span className="text-[var(--ink-soft)]">· {s.preferred_time}</span>
                      {s.delivered_today && (
                        <span data-testid={T.admin.deliveredBadge(s.id)} className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase bg-[var(--ink)] text-[var(--bg)] px-2 py-0.5"><Check size={10}/> Delivered</span>
                      )}
                    </p>
                    <p className="font-serif text-2xl mt-1 flex items-center gap-3 flex-wrap">
                      {s.full_name}
                      {s.is_gift && <span className="text-[10px] tracking-[0.22em] uppercase border border-[var(--sage-deep)] text-[var(--sage-deep)] px-1.5 py-0.5">Gift</span>}
                    </p>
                    {s.is_gift && s.recipient_name && (
                      <p className="italic text-[var(--sage-deep)] text-sm">→ {s.recipient_name}</p>
                    )}
                    <p className="text-sm text-[var(--ink-soft)] mt-2 flex items-start gap-2">
                      <MapPin size={13} className="mt-0.5 shrink-0"/>
                      <span>{s.address}{s.apartment ? ` · ${s.apartment}` : ""}</span>
                    </p>
                    <p className="text-sm text-[var(--ink-soft)] mt-1 flex items-center gap-2">
                      <Phone size={13}/> <a href={`tel:${s.phone}`} className="hover:text-[var(--ink)] transition-colors">{s.phone}</a>
                    </p>
                    <p className="text-xs text-[var(--ink-soft)] mt-1 flex items-center gap-2">
                      <Flower2 size={12}/> {TIER_MAP[s.tier]?.name || s.tier} · {s.frequency}
                    </p>

                    <div className="flex items-center gap-4 mt-4 flex-wrap">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] tracking-[0.22em] uppercase border-b border-[var(--ink)] pb-0.5 hover:opacity-70"
                      >
                        Open in Maps →
                      </a>
                      {!s.delivered_today ? (
                        <button
                          data-testid={T.admin.uploadPhoto(s.id)}
                          onClick={() => setUploadingStop(s)}
                          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase border border-[var(--ink)] px-3 py-1.5 hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors duration-500"
                        >
                          <Camera size={12}/> Upload delivery photo
                        </button>
                      ) : (
                        <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] inline-flex items-center gap-2"><ImageIcon size={12}/> Customer notified</span>
                      )}
                    </div>
                  </li>
                ))}

                <li className="pl-8 relative">
                  <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[var(--sage-deep)]" />
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--ink-soft)]">Return · Studio</p>
                  <p className="font-serif text-xl mt-1">Estimated total drive · {fmtDuration(selectedDay.estimated_drive_time_min)}</p>
                </li>
              </ol>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PhotoUploadDialog
        stop={uploadingStop}
        onClose={() => setUploadingStop(null)}
        onUploaded={async () => { setUploadingStop(null); await refreshDay(); }}
      />
    </div>
  );
}

function PhotoUploadDialog({ stop, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX_PHOTO_BYTES) { toast.error("Photo too large. Please use an image under 2 MB."); return; }
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!preview || !stop) return;
    setLoading(true);
    try {
      await api.post(`/subscriptions/${stop.id}/deliveries`, {
        photo_base64: preview,
        note,
      });
      toast.success("Delivery logged — customer notified.");
      setFile(null); setPreview(""); setNote("");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {stop && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[var(--bg)] border border-[var(--ink)] p-8 sm:p-10"
          >
            <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-3">Delivery photo</p>
            <h3 className="font-serif text-3xl mb-1">Arrived at {stop.full_name}</h3>
            <p className="text-xs text-[var(--ink-soft)] mb-6">{stop.address}</p>

            <input
              ref={inputRef}
              data-testid={T.admin.photoInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => pick(e.target.files?.[0])}
            />

            {!preview && (
              <button
                onClick={() => inputRef.current?.click()}
                className="w-full aspect-[4/3] border border-dashed border-[var(--line-strong)] hover:border-[var(--ink)] transition-colors duration-300 flex flex-col items-center justify-center gap-3 text-[var(--ink-soft)]"
              >
                <Camera size={22} />
                <span className="text-[11px] tracking-[0.22em] uppercase">Tap to capture</span>
              </button>
            )}
            {preview && (
              <div className="relative aspect-[4/3] overflow-hidden border border-[var(--line-strong)]">
                <img src={preview} alt="Delivery preview" className="w-full h-full object-cover" />
                <button
                  onClick={() => { setPreview(""); setFile(null); }}
                  className="absolute top-2 right-2 bg-[var(--bg)] border border-[var(--ink)] px-2 py-1 text-[10px] tracking-[0.22em] uppercase"
                >
                  Retake
                </button>
              </div>
            )}

            <label className="block mt-6">
              <span className="block text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">Note for the member (optional)</span>
              <textarea
                data-testid={T.admin.photoNote}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="editorial-input"
                placeholder="Left with concierge — still frost-fresh."
              />
            </label>

            <div className="flex justify-between items-center mt-8">
              <button onClick={onClose} className="btn-ghost">Cancel</button>
              <button
                data-testid={T.admin.photoSubmit}
                disabled={!preview || loading}
                onClick={upload}
                className="btn-primary"
              >
                {loading ? "Logging…" : "Log delivery + notify"} <Check size={14} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
